(() => {
  if (customElements.get('customer-wishlists')) return;

  class CustomerWishlists extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.dataset.customerId) return;
      this.initialized = true;
      this.labels = JSON.parse(this.querySelector('[data-wishlist-labels]').textContent);
      this.status = this.querySelector('[data-status]');
      this.products = this.querySelector('[data-products]');
      this.lists = this.querySelector('[data-lists]');
      this.dialog = this.querySelector('dialog');
      this.cache = new Map();
      this.generation = 0;
      this.busy = false;
      this.addingToCart = false;
      try {
        this.state = JSON.parse(this.querySelector('[data-wishlist-state]').textContent) || [];
        if (!Array.isArray(this.state) || this.state.some(list =>
          !list || typeof list.id !== 'string' || !list.id.trim() || typeof list.name !== 'string' ||
          !Array.isArray(list.products) || list.products.some(handle => typeof handle !== 'string' || !handle || /\s/.test(handle) || handle.startsWith('gid://'))
        ) || new Set(this.state.map(list => list.id)).size !== this.state.length) throw new Error('Invalid wishlist data');
      } catch {
        this.showStatus(this.labels.invalid);
        this.querySelector('[data-action="mode"]').hidden = true;
        return;
      }
      this.activeId = this.state[0]?.id;
      try { this.activeId = sessionStorage.getItem(`rekal-working-wishlist:${this.dataset.customerId}`) || this.activeId; } catch {}
      this.setMode(this.dataset.defaultMode);
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('change', event => this.syncVariant(event));
      this.addEventListener('input', event => {
        this.cacheQuantity(event);
        this.syncCartControls();
      });
      this.addEventListener('rekal:card-controls-updated', () => this.syncCartControls());
      this.addEventListener('click', event => this.onQuantityClick(event), true);
      this.addEventListener('submit', event => {
        if (!event.target.matches('product-form form')) return;
        event.preventDefault();
        event.stopPropagation();
        this.addToCart([event.target]);
      }, true);
      this.dialog.querySelector('form').addEventListener('submit', event => this.save(event));
      this.dialog.addEventListener('cancel', event => { if (this.busy) event.preventDefault(); });
      this.dialog.addEventListener('click', event => {
        if (this.busy || event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) {
          this.dialog.close();
        }
      });
      this.dialog.addEventListener('close', () => {
        this.pending = null;
        if (this.dialogTrigger?.isConnected) this.dialogTrigger.focus();
        else this.lists.querySelector('[aria-current="true"]')?.focus();
      });
      this.render();
    }

    disconnectedCallback() {
      clearTimeout(this.statusTimer);
      this.generation++;
      this.dialog?.close();
    }

    showStatus(message, duration = 0) {
      clearTimeout(this.statusTimer);
      this.status.textContent = message;
      if (duration) {
        this.statusTimer = setTimeout(() => { this.status.textContent = ''; }, duration);
      }
    }

    setMode(mode) {
      this.dataset.mode = mode === 'mini' ? 'mini' : 'max';
      const button = this.querySelector('[data-action="mode"]');
      button.textContent = this.dataset.mode === 'mini' ? this.labels.max : this.labels.mini;
      button.setAttribute('aria-pressed', String(this.dataset.mode === 'mini'));
    }

    makeButton(action, label, icon) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.action = action;
      button.className = icon ? 'wl__icon' : 'wl__select';
      if (icon) {
        button.setAttribute('aria-label', label);
        button.title = label;
        button.append(this.querySelector(`[data-${icon}-icon]`).content.cloneNode(true));
      } else button.textContent = label;
      return button;
    }

    render() {
      const active = this.state.find(list => list.id === this.activeId) || this.state[0];
      this.activeId = active?.id;
      try {
        const key = `rekal-working-wishlist:${this.dataset.customerId}`;
        if (this.activeId) sessionStorage.setItem(key, this.activeId);
        else sessionStorage.removeItem(key);
      } catch {}
      this.lists.replaceChildren();
      for (const list of this.state) {
        const row = document.createElement('li');
        row.className = 'wl__list';
        row.dataset.listId = list.id;
        const select = this.makeButton('select', `${list.name} (${list.products.length})`);
        select.setAttribute('aria-current', String(list.id === this.activeId));
        row.append(select, this.makeButton('rename', `${this.labels.rename}: ${list.name}`, 'edit'), this.makeButton('delete-list', `${this.labels.deleteList}: ${list.name}`, 'delete'));
        this.lists.append(row);
      }
      this.querySelector('[data-empty-state]').hidden = this.state.length > 0;
      this.querySelector('[data-wishlist-layout]').hidden = this.state.length === 0;
      this.querySelector('[data-active-name]').textContent = active?.name || '—';
      this.querySelector('[data-action="mode"]').hidden = !active?.products.length;
      this.loadProducts(active);
    }

    async productMarkup(handle) {
      if (!this.cache.has(handle)) {
        const request = (async () => {
          const url = new URL(`${this.dataset.root}products/${encodeURIComponent(handle)}`, location.origin);
          url.searchParams.set('section_id', 'wishlist-product');
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 20000);
          try {
            const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
            if (!response.ok) throw new Error('Product request failed');
            const html = await response.text();
            const documentFragment = new DOMParser().parseFromString(html, 'text/html');
            const card = documentFragment.querySelector('[data-wishlist-card]');
            if (!card) throw new Error('Product unavailable');
            // Scripts in fetched fragments must never be executed; assets are loaded by the main section.
            card.querySelectorAll('script').forEach(script => script.remove());
            return card.outerHTML;
          } finally { clearTimeout(timeout); }
        })();
        this.cache.set(handle, request);
        request.catch(() => this.cache.delete(handle));
      }
      return this.cache.get(handle);
    }

    async loadProducts(list) {
      const generation = ++this.generation;
      this.products.replaceChildren();
      this.syncCartControls();
      const handles = [...new Set(list?.products || [])];
      if (!handles.length) {
        const message = document.createElement('p');
        message.className = 'wl__notice';
        message.textContent = list ? this.labels.emptyList : this.labels.empty;
        this.products.append(message);
        this.products.removeAttribute('aria-busy');
        return;
      }
      this.products.setAttribute('aria-busy', 'true');
      const slots = handles.map(handle => {
        const slot = document.createElement('div');
        slot.dataset.handle = handle;
        slot.className = 'wl__notice';
        slot.textContent = this.labels.loading;
        this.products.append(slot);
        return slot;
      });
      let cursor = 0;
      const worker = async () => {
        while (cursor < handles.length && generation === this.generation && this.isConnected) {
          const index = cursor++;
          const handle = handles[index];
          const slot = slots[index];
          try {
            const html = await this.productMarkup(handle);
            if (generation !== this.generation || !this.isConnected) return;
            slot.innerHTML = html;
            slot.className = '';
            const card = slot.querySelector('[data-wishlist-card]');
            const remove = this.makeButton('remove-product', `${this.labels.removeProduct}: ${card.querySelector('h3').textContent.trim()}`, 'delete');
            remove.classList.add('wl-card__remove');
            card.append(remove);
            const form = card.querySelector('product-form form');
            if (form) {
              const errors = document.createElement('div');
              errors.className = 'product-form__error-message-wrapper';
              errors.hidden = true;
              errors.setAttribute('role', 'alert');
              const text = document.createElement('span');
              text.className = 'product-form__error-message';
              errors.append(text);
              form.append(errors);
              form.dataset.quantityCacheKey = this.quantityCacheKey(list.id, handle);
              window.initRekalVariantDropdowns?.(card);
              this.restoreQuantity(form);
              this.syncCartControls();
            }
          } catch {
            if (generation !== this.generation || !this.isConnected) return;
            const message = document.createElement('p');
            message.textContent = `${handle}: ${this.labels.unavailable}`;
            slot.replaceChildren(message, this.makeButton('retry', this.labels.retry), this.makeButton('remove-product', `${this.labels.removeProduct}: ${handle}`, 'delete'));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, handles.length) }, worker));
      if (generation === this.generation) this.products.removeAttribute('aria-busy');
    }

    syncVariant(event) {
      this.cacheQuantity(event);
      this.syncCartControls();
      if (!event.target.matches('[data-card-variant-select]')) return;
      const option = event.target.selectedOptions[0];
      const card = event.target.closest('[data-wishlist-card]');
      if (!option || !card) return;
      card.querySelector('[data-wishlist-sku]').textContent = option.dataset.cardArticleNumber || '—';
      const stock = card.querySelector('[data-wishlist-stock]');
      stock.textContent = stock.dataset.stockMessage || (option.dataset.available === 'true' ? this.labels.inStock : this.labels.soldOut);
    }

    quantityCacheKey(listId, handle) {
      return `rekal-wishlist-quantity:${JSON.stringify([this.dataset.customerId, this.dataset.locationId || '', listId, handle])}`;
    }

    cacheQuantity(event) {
      if (!event.target.matches('[name="quantity"]')) return;
      const form = event.target.closest('form');
      if (form?.dataset.quantityCacheReady !== 'true') return;
      const item = this.cartSelection(form);
      if (!item || !Number.isSafeInteger(item.quantity) || item.quantity < 0) return;
      try {
        localStorage.setItem(form.dataset.quantityCacheKey, JSON.stringify({
          variantId: item.option.value,
          quantity: item.quantity,
        }));
      } catch { /* Cart controls still work when browser storage is unavailable. */ }
    }

    restoreQuantity(form) {
      const select = form.querySelector('[data-card-variant-select]');
      const input = form.querySelector('[name="quantity"]');
      if (!select || !input) return;
      let saved;
      try { saved = JSON.parse(localStorage.getItem(form.dataset.quantityCacheKey)); } catch {}
      if (saved && typeof saved.variantId === 'string' && Number.isSafeInteger(saved.quantity) && saved.quantity >= 0 &&
          Array.from(select.options).some(option => option.value === saved.variantId)) {
        select.value = saved.variantId;
        // Refresh variant metadata and rules before restoring the quantity.
        select.dispatchEvent(new Event('change', { bubbles: true }));
        input.value = String(saved.quantity);
      }
      form.dataset.quantityCacheReady = 'true';
      // Updates prices/buttons and replaces stale cache entries with the current selection.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    cartSelection(form) {
      const input = form.querySelector('[name="quantity"]');
      const option = form.querySelector('[data-card-variant-select]')?.selectedOptions[0];
      const quantity = Number(input?.value);
      if (!input || !option) return null;
      const min = Number(option.dataset.min) || 1;
      const step = Number(option.dataset.step) || 1;
      const max = option.dataset.max ? Number(option.dataset.max) : Infinity;
      const valid = Number.isInteger(quantity) && quantity >= min && quantity <= max && quantity % step === 0;
      return { form, input, option, quantity, valid, available: option.dataset.available === 'true' };
    }

    syncCartControls() {
      let selected = false;
      this.products.querySelectorAll('product-form form').forEach(form => {
        const item = this.cartSelection(form);
        const enabled = item?.available && item.valid && item.quantity > 0;
        const button = form.querySelector('[type="submit"]');
        if (button) button.disabled = this.addingToCart || !enabled;
        if (enabled) selected = true;
      });
      this.querySelector('[data-action="add-selected"]').disabled = this.addingToCart || this.busy || !selected;
    }

    onQuantityClick(event) {
      const button = event.target.closest('.quantity__button');
      if (!button || !this.products.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.addingToCart) return;
      const item = this.cartSelection(button.closest('form'));
      if (!item) return;
      const min = Number(item.option.dataset.min) || 1;
      const step = Number(item.option.dataset.step) || 1;
      const max = item.option.dataset.max ? Number(item.option.dataset.max) : Infinity;
      const current = Number.isFinite(item.quantity) ? item.quantity : 0;
      let next = button.name === 'plus' ? (current < min ? min : current + step) : (current <= min ? 0 : current - step);
      if (next < min) next = 0;
      if (next > max) return;
      item.input.value = next;
      item.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    async addToCart(forms = Array.from(this.products.querySelectorAll('product-form form'))) {
      if (this.addingToCart || this.busy) return;
      const selected = forms.map(form => this.cartSelection(form)).filter(item => item?.available && item.quantity > 0);
      if (!selected.length) return;
      const invalid = selected.find(item => !item.valid);
      if (invalid) {
        this.showStatus(this.labels.invalidQuantity);
        invalid.input.focus();
        return;
      }
      const items = selected.map(({ form, option, quantity }) => {
        const properties = {};
        for (const [key, value] of new FormData(form)) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        }
        return { id: option.value, quantity, properties };
      });
      const controls = Array.from(this.querySelectorAll('button, input, select')).map(control => [control, control.disabled]);
      this.addingToCart = true;
      controls.forEach(([control]) => { control.disabled = true; });
      this.querySelector('[data-action="add-selected"]').setAttribute('aria-busy', 'true');
      this.showStatus(this.labels.adding);
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.().map(section => section.id) || ['cart-icon-bubble'];
      let added = false;
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.labels.cartError);
        added = true;
        selected.forEach(({ input }) => { input.value = '0'; });
        this.showStatus(this.labels.added, 5000);
        // Cart insertion succeeded even if a section cannot be rendered.
        if (drawer && sections.every(id => result.sections?.[id])) {
          drawer.setActiveElement(this.querySelector('[data-active-heading]'));
          drawer.renderContents(result);
          drawer.classList.remove('is-empty');
        } else if (result.sections?.['cart-icon-bubble']) {
          const html = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html');
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = html.querySelector('.shopify-section').innerHTML;
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'customer-wishlists', cartData: result });
      } catch (error) {
        if (!added) this.showStatus(`${this.labels.cartError}${error.message && error.message !== this.labels.cartError ? ` ${error.message}` : ''}`);
        else console.error('Wishlist cart display refresh failed:', error);
      } finally {
        this.addingToCart = false;
        controls.forEach(([control, disabled]) => { control.disabled = disabled; });
        this.querySelector('[data-action="add-selected"]').removeAttribute('aria-busy');
        if (added) selected.forEach(({ input }) => input.dispatchEvent(new Event('change', { bubbles: true })));
        this.syncCartControls();
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button || !this.contains(button) || this.busy || this.addingToCart) return;
      const action = button.dataset.action;
      if (action === 'add-selected') return this.addToCart();
      if (action === 'mode') return this.setMode(this.dataset.mode === 'mini' ? 'max' : 'mini');
      if (action === 'cancel') return this.dialog.close();
      if (action === 'select') {
        this.activeId = button.closest('[data-list-id]').dataset.listId;
        this.render();
        this.querySelector('[data-active-heading]').focus();
        return;
      }
      if (action === 'retry') return this.loadProducts(this.state.find(list => list.id === this.activeId));
      const id = button.closest('[data-list-id]')?.dataset.listId || this.activeId;
      const list = this.state.find(item => item.id === id);
      if (!list) return;
      this.pending = { action, id, handle: button.closest('[data-handle]')?.dataset.handle };
      this.dialogTrigger = button;
      const rename = action === 'rename';
      const input = this.dialog.querySelector('input');
      input.hidden = !rename;
      input.disabled = !rename;
      input.value = list.name;
      this.dialog.querySelector('[data-name-label]').hidden = !rename;
      this.dialog.querySelector('[data-dialog-title]').textContent = rename ? this.labels.rename : action === 'delete-list' ? this.labels.deleteList : this.labels.removeProduct;
      this.dialog.querySelector('[data-dialog-message]').textContent = rename ? '' : `${action === 'delete-list' ? this.labels.confirmDelete : this.labels.confirmRemove} (${list.name})`;
      this.dialog.querySelector('[data-confirm]').textContent = rename ? this.labels.save : this.labels.delete;
      this.dialog.querySelector('[data-dialog-error]').textContent = '';
      this.dialog.showModal();
      if (rename) { input.focus(); input.select(); }
      else this.dialog.querySelector('[data-action="cancel"]').focus();
    }

    async save(event) {
      event.preventDefault();
      if (this.busy || !this.pending) return;
      const { action, id, handle } = this.pending;
      const next = structuredClone(this.state);
      const index = next.findIndex(list => list.id === id);
      if (index < 0) return;
      if (action === 'rename') {
        const name = this.dialog.querySelector('input').value.trim();
        if (!name) { this.dialog.querySelector('input').focus(); return; }
        next[index].name = name;
      } else if (action === 'delete-list') next.splice(index, 1);
      else if (action === 'remove-product') next[index].products = next[index].products.filter(productHandle => productHandle !== handle);
      else return;
      this.busy = true;
      this.dialog.querySelectorAll('button').forEach(button => { button.disabled = true; });
      this.dialog.querySelector('[data-dialog-error]').textContent = '';
      this.showStatus(this.labels.saving);
      try {
        if (!window.api?.fetch) throw new Error('Gadget client unavailable');
        const response = await window.api.fetch('/wishlist/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: this.dataset.customerId, wishlistData: next }),
        });
        if (!response.ok) throw new Error('Wishlist save failed');
        const result = await response.json();
        if (result.errorObject || !result.message) throw new Error('Invalid save response');
        if (action === 'delete-list' || action === 'remove-product') {
          const handles = action === 'delete-list' ? this.state[index].products : [handle];
          for (const productHandle of handles) {
            try { localStorage.removeItem(this.quantityCacheKey(id, productHandle)); } catch {}
          }
        }
        this.state = next;
        this.querySelector('[data-wishlist-state]').textContent = JSON.stringify(next);
        this.render();
        this.showStatus(this.labels.saved, 5000);
        this.dialog.close();
      } catch {
        this.showStatus(this.labels.error);
        this.dialog.querySelector('[data-dialog-error]').textContent = this.labels.error;
      } finally {
        this.busy = false;
        this.dialog.querySelectorAll('button').forEach(button => { button.disabled = false; });
      }
    }
  }
  customElements.define('customer-wishlists', CustomerWishlists);
})();
